import { IntelligemsDomEvents, dispatchIntelligemsEvent } from '@theme/intelligems-events';
/* AB-TEST: retirer ces 2 imports en fin de test (ainsi que assets/ab-variant-indexes.js). */
import { ThemeEvents } from '@theme/events';
import { pickDesiredVariantIndex, resolveAbVariantRule } from './ab-variant-indexes.js';

/**
 * Sélecteur multi-produits (tailles / gammes liées).
 *
 * - redirect : navigation classique
 * - swap : fetch HTML du produit cible, sans rechargement
 *   · fiche produit (grouped) → colonne `.product-section__product-info` + galerie
 *   · produit en vedette (LP) → formulaire + galerie uniquement
 */
class ProductSelectors extends HTMLElement {
  #abortFetch = null;
  #changeAbort = null;
  /* AB-TEST */
  #abAbort = null;
  #abHandle = '';
  #abDispatches = 0;

  connectedCallback() {
    this.#assignRadioGroupNames();

    if (!this.classList.contains('is-fetching')) {
      this.#syncSelectedFromSection();
    }

    this.#changeAbort?.abort();
    this.#changeAbort = new AbortController();
    this.addEventListener('change', this.#onSelectionChange, { signal: this.#changeAbort.signal });

    /* AB-TEST */
    this.#bindAbVariants();
    this.#scheduleAbVariants();
  }

  disconnectedCallback() {
    this.#abortFetch?.abort();
    this.#changeAbort?.abort();
    this.#changeAbort = null;
    /* AB-TEST */
    this.#abAbort?.abort();
    this.#abAbort = null;
  }

  #onSelectionChange = (event) => {
    const input = event.target.closest('[data-product-selector-input]');
    if (!input || input.type !== 'radio' || !input.checked) return;

    dispatchIntelligemsEvent(this, IntelligemsDomEvents.productSelectorChange, {
      handle: input.dataset.productHandle,
    });
    this.#loadProductForInput(input);
  };

  #loadProductForInput(input) {
    const handle = input.dataset.productHandle?.trim();
    if (!handle || this.hasAttribute('data-visual-preview-mode')) return;

    const rootUrl = this.#normalizeRootUrl(this.dataset.rootUrl);

    if (this.dataset.navigationMode === 'redirect') {
      this.#redirectToProduct(input, rootUrl);
      return;
    }

    const ctx = this.#resolveSwapContext();
    if (!ctx?.productSection) {
      console.error('[product-selectors] Conteneur .product-section introuvable.');
      return;
    }

    const { productSection } = ctx;
    const previousHandle = productSection.dataset.activeProductHandle?.trim();

    if (previousHandle === handle) return;

    productSection.dataset.activeProductHandle = handle;
    this.#setSelectedState(handle);

    this.#abortFetch?.abort();
    this.#abortFetch = new AbortController();

    this.classList.add('is-fetching');

    void this.#fetchProductSection(input, rootUrl, this.#abortFetch.signal)
      .then((freshSection) => {
        this.#applySwap(ctx, freshSection, handle);
        this.#syncBrowserUrl(input, rootUrl);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('[product-selectors]', error);
        if (previousHandle) {
          productSection.dataset.activeProductHandle = previousHandle;
          this.#setSelectedState(previousHandle);
        }
      })
      .finally(() => {
        this.classList.remove('is-fetching');
      });
  }

  #applySwap(ctx, freshSection, handle) {
    const currentSection = ctx.productSection;
    const liveSelector = currentSection.querySelector('product-selectors') || this;

    const formOnly =
      ctx.mode === 'featured' && this.#swapFormOnly(currentSection, freshSection, liveSelector);

    if (!formOnly) {
      this.#swapProductInfoColumn(currentSection, freshSection, liveSelector);
    }

    this.#replaceMediaGallery(currentSection, freshSection);
    this.#finalizeSwitch(currentSection, handle);
  }

  /** LP : ne remplace que le contenu du formulaire. */
  #swapFormOnly(currentSection, freshSection, liveSelector) {
    const currentForm = currentSection.querySelector('form.product-form__form');
    const freshForm = freshSection.querySelector('form.product-form__form');
    if (!currentForm || !freshForm) return false;

    const importedForm = document.importNode(freshForm, true);
    this.#injectLiveSelector(importedForm, liveSelector);
    currentForm.replaceChildren(...importedForm.childNodes);

    for (const attr of freshForm.attributes) {
      currentForm.setAttribute(attr.name, attr.value);
    }

    return true;
  }

  /** PDP grouped : remplace toute la colonne info (textes, accordions, formulaire…). */
  #swapProductInfoColumn(currentSection, freshSection, liveSelector) {
    const currentInfo = currentSection.querySelector('.product-section__product-info');
    const freshInfo = freshSection.querySelector('.product-section__product-info');

    if (!currentInfo || !freshInfo) {
      throw new Error('Colonne .product-section__product-info introuvable.');
    }

    const freshInfoClone = document.importNode(freshInfo, true);

    for (const duplicate of freshInfoClone.querySelectorAll('product-selectors')) {
      duplicate.remove();
    }

    freshInfoClone.querySelector('form.product-form__form')?.prepend(liveSelector);
    currentInfo.replaceWith(freshInfoClone);
  }

  /** Garde le composant actif dans un formulaire fraîchement importé (mode LP). */
  #injectLiveSelector(form, liveSelector) {
    if (!form || !liveSelector) return;

    for (const duplicate of form.querySelectorAll('product-selectors')) {
      duplicate.remove();
    }

    form.prepend(liveSelector);
  }

  #replaceMediaGallery(currentSection, freshSection) {
    const currentMedias = currentSection.querySelector('.product-section__medias');
    const freshMedias = freshSection.querySelector('.product-section__medias');
    if (!currentMedias || !freshMedias) return;

    currentMedias.replaceWith(document.importNode(freshMedias, true));
  }

  #finalizeSwitch(section, handle) {
    section.dataset.activeProductHandle = handle.trim();
    this.#assignRadioGroupNames();
    this.#setSelectedState(handle);

    requestAnimationFrame(() => {
      const productForm = section.querySelector('product-form');
      /* AB-TEST: en fin de test, revenir à l'appel direct ci-dessous, sans le if.
         const hiddenVariant = productForm?.querySelector('form input[name="id"][type="hidden"]');
         hiddenVariant?.dispatchEvent(new Event('change', { bubbles: true }));
      */
      const notified = this.applyAbVariants();

      if (!notified) {
        const hiddenVariant = productForm?.querySelector('form input[name="id"][type="hidden"]');
        hiddenVariant?.dispatchEvent(new Event('change', { bubbles: true }));
      }

      productForm?.dispatchEvent(new Event('quickAddContentLoaded', { bubbles: true }));
    });
  }

  async #fetchProductSection(input, rootUrl, signal) {
    const productPageUrl = this.#buildProductPageUrl(input, rootUrl);
    if (!productPageUrl) {
      throw new Error('Chemin produit introuvable (handle / data-product-url).');
    }

    const response = await fetch(productPageUrl, {
      signal,
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) {
      throw new Error(`Page produit HTTP ${response.status} (${productPageUrl})`);
    }

    const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
    const freshSection =
      parsed.querySelector('.product-section') ||
      parsed.querySelector('.product-section__content')?.closest('.product-section') ||
      parsed.querySelector('.shopify-section .product-section') ||
      parsed.body?.querySelector('.product-section');

    if (!freshSection) {
      throw new Error(`Impossible d'extraire .product-section (${productPageUrl}).`);
    }

    return freshSection;
  }

  #redirectToProduct(input, rootUrl) {
    const nextUrl = this.#buildProductPageUrl(input, rootUrl);
    if (!nextUrl) return;

    const current = `${window.location.pathname}${window.location.search}`;
    const target = `${new URL(nextUrl).pathname}${new URL(nextUrl).search}`;

    if (current === target) return;

    window.location.assign(nextUrl);
  }

  #buildProductPageUrl(input, rootUrl) {
    const pathname = this.#getProductPathname(input, rootUrl);
    if (!pathname) return null;

    const url = new URL(pathname, window.location.origin);
    const view = new URL(window.location.href).searchParams.get('view')?.trim();

    if (view) url.searchParams.set('view', view);

    return url.toString();
  }

  #syncBrowserUrl(input, rootUrl) {
    const nextUrl = this.#buildProductPageUrl(input, rootUrl);
    if (!nextUrl) return;

    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const next = `${new URL(nextUrl).pathname}${new URL(nextUrl).search}${window.location.hash}`;

    if (current !== next) {
      window.history.replaceState(window.history.state, '', next);
    }
  }

  #getProductPathname(input, rootUrl) {
    const handle = input.dataset.productHandle;
    const fromLiquid = input.dataset.productUrl?.trim();
    let pathname = '';

    if (fromLiquid) {
      try {
        pathname = fromLiquid.startsWith('http')
          ? new URL(fromLiquid).pathname
          : fromLiquid.split('?')[0];
      } catch {
        pathname = '';
      }
    }

    if (!pathname && handle) {
      const root = rootUrl.replace(/\/$/, '');
      pathname = `${root}/products/${encodeURIComponent(handle)}`.replace(/\/{2,}/g, '/');
    }

    if (pathname && !pathname.startsWith('/')) {
      pathname = `/${pathname}`;
    }

    return pathname || null;
  }

  #normalizeRootUrl(raw) {
    const root = raw || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  #resolveSwapContext() {
    const featuredRoot = this.closest('.product-featured-section');
    const featuredSwap = featuredRoot?.querySelector('[data-ref="product-featured-swap-root"]');

    if (featuredRoot && featuredSwap) {
      return {
        mode: 'featured',
        productSection: featuredSwap.querySelector('.product-section'),
      };
    }

    const productSection = this.closest('.product-section');
    if (!productSection) return null;

    return { mode: 'standalone', productSection };
  }

  #syncSelectedFromSection() {
    const handle = this.#resolveSwapContext()?.productSection?.dataset.activeProductHandle?.trim();
    if (handle) this.#setSelectedState(handle);
  }

  #setSelectedState(selectedHandle) {
    const normalized = selectedHandle?.trim();
    for (const input of this.querySelectorAll('[data-product-selector-input]')) {
      input.checked = input.dataset.productHandle?.trim() === normalized;
    }
  }

  /* AB-TEST: début — supprimer jusqu'au marqueur « AB-TEST: fin ». */
  /**
   * Applique la règle A/B du produit actif : masque des variantes par index
   * et coche l'index par défaut. À rappeler après injection du script d'A/B.
   * @returns {boolean} true si un changement de variante a été envoyé
   */
  applyAbVariants() {
    return this.#paintAbVariants({ selectDefault: true });
  }

  #bindAbVariants() {
    this.#abAbort?.abort();
    this.#abAbort = new AbortController();
    const { signal } = this.#abAbort;

    document.addEventListener('fullstack:ab-variants', () => this.applyAbVariants(), { signal });
    document.addEventListener(ThemeEvents.variantUpdated, this.#onAbVariantUpdated, { signal });
  }

  #scheduleAbVariants() {
    if (this.classList.contains('is-fetching') || this.hasAttribute('data-visual-preview-mode')) return;

    const run = () => this.applyAbVariants();

    if (!this.#variantPicker()) return;

    if (customElements.get('variant-picker')) {
      requestAnimationFrame(run);
      return;
    }

    customElements.whenDefined('variant-picker').then(() => {
      requestAnimationFrame(run);
    });
  }

  #onAbVariantUpdated = (event) => {
    const section = this.#resolveSwapContext()?.productSection;
    if (!section?.contains(event.target)) return;

    this.#paintAbVariants({ selectDefault: false });
  };

  #variantPicker() {
    return this.#resolveSwapContext()?.productSection?.querySelector('variant-picker') || null;
  }

  #activeSelectorInput() {
    const inputs = [...this.querySelectorAll('[data-product-selector-input]')];
    const handle = this.#resolveSwapContext()?.productSection?.dataset.activeProductHandle?.trim();
    if (!handle) return inputs.find((input) => input.checked) || null;

    return inputs.find((input) => input.dataset.productHandle?.trim() === handle) || null;
  }

  #currentAbRule() {
    const input = this.#activeSelectorInput();
    if (!input) return null;

    const inputs = [...this.querySelectorAll('[data-product-selector-input]')];

    return resolveAbVariantRule({
      handle: input.dataset.productHandle?.trim(),
      index: inputs.indexOf(input),
      dataset: input.dataset,
      table: window.fullstackAb?.productSelectors,
    });
  }

  #paintAbVariants({ selectDefault }) {
    if (this.hasAttribute('data-visual-preview-mode')) return false;

    const rule = this.#currentAbRule();
    if (!rule) return false;

    const handle = this.#activeSelectorInput()?.dataset.productHandle?.trim() || '';
    if (handle !== this.#abHandle) {
      this.#abHandle = handle;
      this.#abDispatches = 0;
    }

    const picker = this.#variantPicker();
    if (!picker) return false;

    let notified = false;

    for (const group of picker.querySelectorAll('.variant-picker__option')) {
      const changed = this.#paintAbGroup(picker, group, rule, selectDefault && !notified);
      if (changed) notified = true;
    }

    /* AB-TEST: le sticky a pu copier le picker avant le filtre. */
    this.#refreshStickyVariantOptions();

    return notified;
  }

  #refreshStickyVariantOptions() {
    const section = this.#resolveSwapContext()?.productSection;
    section?.querySelector('sticky-add-to-cart')?.refreshVariantOptions?.();
  }

  #paintAbGroup(picker, group, rule, selectDefault) {
    const buttons = [...group.querySelectorAll(':scope > .variant-picker__option-values > .variant-picker__option-value')];

    if (buttons.length) {
      return this.#paintAbButtons(picker, buttons, rule, selectDefault);
    }

    const select = group.querySelector('select');
    if (!select) return false;

    return this.#paintAbSelect(picker, select, rule, selectDefault);
  }

  #optionIndex(element, domIndex) {
    const stamped = element.dataset.variantIndex;
    if (stamped != null && stamped !== '') return Number(stamped);

    return domIndex;
  }

  #paintAbButtons(picker, buttons, rule, selectDefault) {
    const entries = buttons.map((element, domIndex) => ({
      element,
      input: element.querySelector('input'),
      index: this.#optionIndex(element, domIndex),
    }));

    const currentIndex = entries.find((entry) => entry.input?.checked)?.index ?? -1;
    const desired = selectDefault
      ? pickDesiredVariantIndex({
          count: this.#optionCount(entries),
          show: rule.show,
          selected: rule.selected,
          currentIndex,
        })
      : null;

    let target = null;

    for (const entry of entries) {
      const visible = !rule.show || rule.show.includes(entry.index);
      entry.element.classList.toggle('is-ab-hidden', !visible);

      if (!(entry.input instanceof HTMLInputElement)) continue;

      entry.input.disabled = !visible;
      if (visible && entry.index === desired) target = entry.input;
    }

    if (!selectDefault) {
      const checked = entries.find((entry) => entry.input?.checked);
      if (checked && rule.show && !rule.show.includes(checked.index)) {
        target = entries.find((entry) => !entry.element.classList.contains('is-ab-hidden') && entry.input && !entry.input.disabled)?.input || null;
        return this.#commitAbInput(picker, target);
      }

      return false;
    }

    return this.#commitAbInput(picker, target);
  }

  #paintAbSelect(picker, select, rule, selectDefault) {
    const options = [...select.options];
    const entries = options.map((option, domIndex) => ({
      option,
      index: this.#optionIndex(option, domIndex),
    }));
    const currentIndex = entries[select.selectedIndex]?.index ?? -1;
    const desired = selectDefault
      ? pickDesiredVariantIndex({
          count: this.#optionCount(entries),
          show: rule.show,
          selected: rule.selected,
          currentIndex,
        })
      : null;

    let targetIndex = -1;

    for (const entry of entries) {
      const visible = !rule.show || rule.show.includes(entry.index);
      entry.option.classList.toggle('is-ab-hidden', !visible);
      entry.option.disabled = !visible;
      entry.option.hidden = !visible;
      if (visible && entry.index === desired) targetIndex = entry.index;
    }

    if (!selectDefault) {
      const current = entries.find((entry) => entry.option.selected);
      if (current && rule.show && !rule.show.includes(current.index)) {
        const fallback = entries.find((entry) => !entry.option.disabled);
        if (!fallback) return false;
        select.selectedIndex = options.indexOf(fallback.option);
        return this.#commitAbInput(picker, select);
      }

      return false;
    }

    const target = entries.find((entry) => entry.index === targetIndex)?.option;
    if (!target) return false;

    const already = select.selectedIndex === options.indexOf(target);
    select.value = target.value;
    target.selected = true;

    if (already) return false;

    return this.#commitAbInput(picker, select);
  }

  #optionCount(entries) {
    return entries.reduce((max, entry) => Math.max(max, entry.index + 1), 0);
  }

  #commitAbInput(picker, input) {
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return false;

    const hidden = picker.querySelector('input[name="id"]');
    const variantId =
      input instanceof HTMLSelectElement ? input.selectedOptions[0]?.dataset.variantId : input.dataset.variantId;
    const already =
      input instanceof HTMLInputElement
        ? input.checked && (!variantId || hidden?.value === variantId)
        : Boolean(variantId) && hidden?.value === variantId;

    if (input instanceof HTMLInputElement) input.checked = true;
    if (hidden && variantId) hidden.value = variantId;
    if (already || this.#abDispatches >= 2) return false;

    this.#abDispatches += 1;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  /* AB-TEST: fin */

  #assignRadioGroupNames() {
    const groupId = this.dataset.selectorGroupId;
    if (!groupId) return;

    const groupName = `product-selector-${groupId}`;
    for (const input of this.querySelectorAll('[data-product-selector-input]')) {
      input.name = groupName;
    }
  }
}

if (!customElements.get('product-selectors')) {
  customElements.define('product-selectors', ProductSelectors);
}

function hydrateProductSelectorRadios() {
  for (const host of document.querySelectorAll('product-selectors[data-selector-group-id]')) {
    const groupId = host.dataset.selectorGroupId;
    if (!groupId) continue;

    const groupName = `product-selector-${groupId}`;
    for (const input of host.querySelectorAll('[data-product-selector-input]')) {
      input.name = groupName;
    }
  }
}

hydrateProductSelectorRadios();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hydrateProductSelectorRadios, { once: true });
}
document.addEventListener('shopify:section:load', hydrateProductSelectorRadios);
